import withStore, { TLoadingAwarePresenter, TPresentable } from "../withStore";
import Application from "../../Application";
import { IPositionType } from "../../model/position/PositionType";
import CreatePositionTypeInteractor from "../../interactor/position/CreatePositionTypeInteractor";
import GetPositionTypesInteractor from "../../interactor/position/GetPositionTypesInteractor";
import UpdatePositionTypeInteractor from "../../interactor/position/UpdatePositionTypeInteractor";
import ShowSuccessMessageInteractor from "../../interactor/notifications/ShowSuccessMessageInteractor";
import ShowErrorMessageInteractor from "../../interactor/notifications/ShowErrorMessageInteractor";
import PositionTypePresentationModel, {
  TPositionTypePresentationModel
} from "./model/PositionTypePresentationModel";

export interface TPositionTypesPresenter extends TLoadingAwarePresenter {
  positionTypes: TPositionTypePresentationModel[] | undefined;
}
export interface IPositionTypesPresenter
  extends TPositionTypesPresenter,
    TPresentable {
  setPositionTypes(positionType: IPositionType[] | undefined): void;
  createPositionType(positionType: IPositionType): void;
  getPositionTypes(): void;
  updatePositionType(positionType: IPositionType): void;
}

export interface IPositionTypePresentationModel extends IPositionType {
  key: number;
}

const defaultState: TPositionTypesPresenter = {
  positionTypes: undefined
};

const PositionTypesPresenter = withStore<
  IPositionTypesPresenter,
  TPositionTypesPresenter
>(({ application, store, loader, translate }): IPositionTypesPresenter => {
  const _store = store;
  const _application: Application = application;
  const state = _store.getState<TPositionTypesPresenter>();

  loader.start("positionTypesLoader");

  const setPositionTypes = (positionTypes: IPositionType[]) => {
    _store.update({
      positionTypes: positionTypes && [
        ...positionTypes.map(PositionTypePresentationModel)
      ]
    });
    loader.stop("positionTypesLoader");
  };

  const getPositionTypes = async () => {
    try {
      let positionTypes = await _application.container
        .resolve<GetPositionTypesInteractor>("getPositionTypes")
        .execute();
      _store.update({
        positionTypes: [...positionTypes.map(PositionTypePresentationModel)]
      });
    } catch (error) {
      _application.container
        .resolve<ShowErrorMessageInteractor>("showErrorMessage")
        .execute((error as any).message);
    }
  };

  const createPositionType = async (positionType: IPositionType) => {
    try {
      await _application.container
        .resolve<CreatePositionTypeInteractor>("createPositionType")
        .execute(positionType);
      _application.container
        .resolve<ShowSuccessMessageInteractor>("showSuccessMessage")
        .execute(
          translate("position_types_page.messages.create_positionType_success")
        );
      await getPositionTypes();
    } catch (error) {
      _application.container
        .resolve<ShowErrorMessageInteractor>("showErrorMessage")
        .execute((error as any).message);
    }
  };

  const updatePositionType = async (positionType: IPositionType) => {
    try {
      await _application.container
        .resolve<UpdatePositionTypeInteractor>("updatePositionType")
        .execute(positionType);
      _application.container
        .resolve<ShowSuccessMessageInteractor>("showSuccessMessage")
        .execute(
          translate("position_types_page.messages.update_positionType_success")
        );
      await getPositionTypes();
    } catch (error) {
      _application.container
        .resolve<ShowErrorMessageInteractor>("showErrorMessage")
        .execute((error as any).message);
    }
  };

  return {
    ...state,
    store: _store,
    loader,
    application: _application,
    translate,
    setPositionTypes,
    createPositionType,
    getPositionTypes,
    updatePositionType
  };
}, defaultState);

export default PositionTypesPresenter;
